// O que o servidor precisa saber sobre a trilha do aluno, e o que fazer com o
// que ele devolve. Funções PURAS: recebem dados, devolvem dados.
//
// Nada aqui faz HTTP, e isso é regra do módulo, não preferência: `TrilhaLudica`
// é autocontido e nenhum import sai dele (ver ../README.md). Quem tem axios, a
// URL da API e o token é o host — `components/ItensDaAtividade`, a única peça
// fora do módulo. Ele recebe o corpo pronto daqui e manda.
//
// A alternativa seria importar `config/api` e `config/axios` aqui dentro, o que
// tornaria o módulo impossível de plugar noutro host sem arrastar o AVA junto.

import { cardsDoAlbum } from "../nucleo/album";

/**
 * Junta o que veio do servidor com o que está no navegador.
 *
 * O SERVIDOR MANDA quando existe: é ele que sobrevive à troca de dispositivo. O
 * `localStorage` é cache — vale enquanto o servidor não respondeu, e é o que
 * mantém a trilha jogável offline.
 *
 * `null` do servidor é significativo: quer dizer PRIMEIRA ENTRADA nesta trilha,
 * e é o que faz o aluno cair no onboarding e o sorteio ser derivado.
 *
 * @param {object|null} doServidor  `trailExperience` do payload da atividade
 * @param {object}      local       o que `carregarProgressoLocal` devolveu
 * @returns {object} progresso no formato local
 */
export function juntarProgresso(doServidor, local) {
  if (!doServidor) return local;

  return {
    ...local,
    // `??` e não `||`: avatar vazio e `introSeen: false` são respostas válidas
    // do servidor, e com `||` cairiam silenciosamente no valor local.
    avatarId: doServidor.avatarId ?? local.avatarId,
    introVisto: doServidor.introSeen ?? local.introVisto,
    encerramentoVisto: doServidor.endingSeen ?? local.encerramentoVisto,
    // O sorteio do servidor tem precedência absoluta: é ele que congela o que o
    // aluno viu. Só cai no derivado se o servidor não tiver nada guardado.
    //
    // Vale para os COMUNS e só para eles: o sorteio das RARAS é derivado a cada
    // carga e não passa por aqui nem pelo `localStorage` (ver
    // `derivarSorteioRaras` em ./progressoDoAluno.js). Um sorteio que não é
    // guardado não tem o que reconciliar.
    sorteioComuns: temSorteio(doServidor.drawnCards)
      ? doServidor.drawnCards
      : local.sorteioComuns
  };
}

function temSorteio(sorteio) {
  return Boolean(sorteio && Object.keys(sorteio).length > 0);
}

/**
 * O corpo do POST que salva a trilha e reconcilia a coleção.
 *
 * `collected` é a lista COMPLETA do que o aluno já deveria ter — fases
 * concluídas cruzadas com o sorteio —, e não um evento por carta. Mandar tudo a
 * cada carga é o que torna a coleção idempotente e auto-corretiva: um POST
 * perdido se conserta sozinho na próxima vez que ele abrir a trilha.
 *
 * A alternativa, gravar quando o aluno fecha a cerimônia, já foi tentada no
 * `localStorage` e falhou — recarregar a página com o modal aberto perdia a
 * carta para sempre.
 *
 * `drawn_cards` continua sendo SÓ o sorteio dos comuns, e o nome já era genérico
 * antes de existir um segundo sorteio. Não vira `{comuns, raras}`: é chave de uma
 * coluna do servidor, lida por `juntarProgresso` na volta, e o das raras é derivado
 * — mandá-lo seria guardar o que se decidiu não guardar.
 *
 * @param {object} params
 * @param {number} params.activityId
 * @param {object} params.local        progresso (avatar, telas vistas)
 * @param {object} params.trilha       modelo neutro, com as fases e o que já concluiu
 * @param {object} params.regiao       conteúdo da região
 * @param {object} params.sorteio      { comuns, raras } (ver nucleo/recompensaDaFase)
 * @returns {object} corpo pronto para o POST
 */
export function corpoDeSincronia({
  activityId,
  local,
  trilha,
  regiao,
  sorteio = {}
}) {
  return {
    activity_id: activityId,
    avatar_id: local.avatarId || null,
    drawn_cards: sorteio.comuns || {},
    intro_seen: Boolean(local.introVisto),
    ending_seen: Boolean(local.encerramentoVisto),
    collected: cartasConquistadas({ trilha, regiao, sorteio })
  };
}

/**
 * As cartas que o aluno já conquistou nesta trilha, no formato do servidor.
 *
 * Deriva de `cardsDoAlbum`, o mesmo cálculo que monta o álbum do encerramento —
 * de propósito. Se fossem dois cálculos, a coleção e o álbum divergiriam, e a
 * divergência apareceria como carta que está num e não no outro.
 *
 * Desde 24/08/2026 isso inclui a fase rara cuja carta NÃO foi sorteada: ela não
 * entra aqui, e é o mesmo `cardsDoAlbum` que a deixa de fora. A cena do arco
 * aconteceu na tela e não gera linha no servidor — não há colecionável para gravar.
 */
function cartasConquistadas({ trilha, regiao, sorteio }) {
  if (!trilha || !trilha.fases || !regiao) return [];

  const cards = cardsDoAlbum(trilha.fases, trilha.totalFases, regiao, sorteio);

  return cards.map(card => ({
    card_id: card.id,
    region: regiao.id,
    rarity: card.tipo === "raro" ? "raro" : "comum",
    phase: faseDoCard(card.id, sorteio.comuns)
  }));
}

/**
 * Em que fase a carta caiu, quando dá para saber.
 *
 * Só as comuns estão no sorteio; as raras caem em posição calculada e voltam
 * `null`. É proveniência, não regra — o servidor guarda para a área de
 * conquistas poder dizer "ganha na fase 4", e nada depende disso.
 */
function faseDoCard(cardId, sorteio) {
  if (!sorteio) return null;
  const fase = Object.keys(sorteio).find(numero => sorteio[numero] === cardId);
  return fase ? Number(fase) : null;
}
